import { canUseDOM } from 'exenv';
import { useEffect, useMemo, useState } from 'react';
import { Breakpoint, BreakpointVisibilityMap } from './types';
import { BREAKPOINT_ORDER } from './utils';

/**
 * Custom hook to get the current value based on media queries.
 *
 * **Note:** This hook is not SSR compatible because it uses `window.matchMedia`, which is not available on the server.
 *
 * @param {string[]} queries - An array of media query strings.
 * @param {T[]} values - An array of values corresponding to the media queries.
 * @param {T} defaultValue - The default value to be used if none of the media queries match.
 * @returns {T} - The value corresponding to the first matching media query, or the default value.
 */
export const useMedia = <T>(queries: string[], values: T[], defaultValue: T) => {
  const mediaQueryLists = queries.map((q) => canUseDOM && window.matchMedia(q));

  const getValue = () => {
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    return values?.[index] || defaultValue;
  };

  const [value, setValue] = useState<T>(getValue);

  useEffect(() => {
    const handler = () => setValue(getValue);
    mediaQueryLists.forEach((mql) => mql.addListener(handler));
    return () => mediaQueryLists.forEach((mql) => mql.removeListener(handler));
  }, []);

  return value;
};

/**
 * Custom hook to get the current breakpoint based on screen width.
 *
 * **Note:** This hook is not SSR compatible because it uses `window.matchMedia`, which is not available on the server.
 */
export const useCurrentBreakpoint = () =>
  useMedia<Breakpoint>(
    [
      '(min-width: 1280px)',
      '(min-width: 1024px)',
      '(min-width: 768px)',
      '(min-width: 600px)',
      '(min-width: 480px)',
      '(min-width: 0px)'
    ],
    BREAKPOINT_ORDER,
    'xl'
  );

export const useBreakpointVisibility = (): BreakpointVisibilityMap => {
  const currentBreakpoint = useCurrentBreakpoint();

  const breakpoints = useMemo(() => {
    const currentIndex = BREAKPOINT_ORDER.indexOf(currentBreakpoint);

    return BREAKPOINT_ORDER.reduce(
      (acc, breakpoint, index) => {
        acc[breakpoint] = index >= currentIndex;
        return acc;
      },
      {} as Record<Breakpoint, boolean>
    );
  }, [currentBreakpoint]);

  return breakpoints;
};

export const useIsTouchDevice = () => {
  const isTouchDevice = useMedia(['(hover: none) and (pointer: coarse)'], [true], false);
  return isTouchDevice;
};

export const useIsTouchDeviceAndLessThan = (breakpoint: Breakpoint) => {
  const isTouchDevice = useIsTouchDevice();
  const currentBreakpoint = useCurrentBreakpoint();
  const currentIndex = BREAKPOINT_ORDER.indexOf(currentBreakpoint);
  const targetIndex = BREAKPOINT_ORDER.indexOf(breakpoint);
  return isTouchDevice && currentIndex > targetIndex;
};
